import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { hoverHelpers } from 'shared-helpers';

import { getEntityProfileUrl, getAmountValue } from '/src/components/ICESections/Investments/helpers';
import { renderProxyAmount } from '/src/pages/directories/helpers/fundingRowHelpers';

import useOtherInvestorsModal from '/src/components/ICESections/Investments/Hooks/useOtherInvestorsModal';
import OtherInvestorAmount from '/src/components/ICESections/Investments/Components/OtherInvestorAmount';
import EstimationIndicator from '../../../Reusable/EstimationIndicator';
import { MOB_BADGE_COLORS, getInitials } from '/src/components/ICESections/Shared/mobileListUtils';


const OtherInvestorsModal = ({ activeRow, onClose, hasDirectoryAccess }) => {
    const { contentRef, contentScrollState } = useOtherInvestorsModal({
        activeRow,
        onClose,
    });

    const [expandedRows, setExpandedRows] = useState([]);
    const toggleRow = (key) => setExpandedRows(prev =>
        prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );

    if (!activeRow) {
        return null;
    }

    const dialogClassName = [
        'lps-modal__dialog',
        contentScrollState.hasMoreAbove && 'lps-modal__dialog--scrolled',
    ].filter(Boolean).join(' ');

    return ReactDOM.createPortal(
        <div
            className="lps-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lps-modal-title"
        >
            <button
                type="button"
                className="lps-modal__backdrop"
                aria-label="Close other investors modal"
                onClick={onClose}
            />

            <div className={dialogClassName}>
                <div className="lps-modal__header">
                    <div className="lps-modal__header-title-row">
                        <h3 id="lps-modal-title">Investors</h3>
                        <button
                            type="button"
                            className="lps-modal__close"
                            aria-label="Close"
                            onClick={onClose}
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <EstimationIndicator />

                    <div className="metric-card-row">
                        <div className="metric-card">
                            <span className="metric-card-label">Startup</span>
                            <span className="metric-card-val">
                                <a
                                    className="red"
                                    href={getEntityProfileUrl(activeRow?.startup)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onMouseEnter={(event) => hoverHelpers.handleEntityHover(activeRow?.startup, event, hasDirectoryAccess)}
                                    onMouseLeave={() => hoverHelpers.handleMouseOut()}
                                >
                                    {activeRow?.startup?.name || 'N/A'}
                                </a>
                            </span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-card-label">Stage</span>
                            <span className="metric-card-val">{activeRow?.stage?.name || 'N/A'}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-card-label">Investment Vehicle</span>
                            <span className="metric-card-val">{activeRow?.investmentVehicle || 'N/A'}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-card-label">Amount</span>
                            <span className="metric-card-val">
                                {activeRow.amount
                                    ? getAmountValue(activeRow.amount)
                                    : renderProxyAmount(activeRow.undisclosedAmount) || 'Undisclosed'
                                }
                            </span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-card-label">Total Investors</span>
                            <span className="metric-card-val">
                                {activeRow?.otherInvestors?.length != null ? `${activeRow.otherInvestors.length} Investor${activeRow.otherInvestors.length !== 1 ? 's' : ''}` : 'N/A'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="lps-modal__table-shell">
                    <div
                        ref={contentRef}
                        className="lps-modal__content"
                    >
                        <table className="lps-modal__table">
                            <thead>
                                <tr className="exits-table-header">
                                    <th>Investor Name</th>
                                    <th>HQ</th>
                                    <th>Fund Name</th>
                                    <th>Lead Investor</th>
                                    <th>Invested Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeRow?.otherInvestors?.map((investor) => {
                                    const investorUrl = getEntityProfileUrl(investor);

                                    return (
                                        <tr key={investor?.key}>
                                            <td>
                                                {investorUrl ? (
                                                    <a
                                                        href={investorUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onMouseEnter={(event) => {
                                                            hoverHelpers.handleEntityHover(investor, event, hasDirectoryAccess);
                                                        }}
                                                        onMouseLeave={() => hoverHelpers.handleMouseOut()}
                                                    >
                                                        {investor?.name}
                                                    </a>
                                                ) : (
                                                    <span
                                                        className="startup-name"
                                                        data-action="expand-slider"
                                                        data-entityid={investor?.id}
                                                        data-ecosystemtype={investor?.type?.toLowerCase()}
                                                        onMouseEnter={(event) => {
                                                            hoverHelpers.handleEntityHover(investor, event, hasDirectoryAccess);
                                                        }}
                                                        onMouseLeave={() => hoverHelpers.handleMouseOut()}
                                                    >
                                                        {investor?.name}
                                                    </span>
                                                )}
                                            </td>
                                            <td><span>{investor?.hq}</span></td>
                                            <td><span>{investor?.fundName}</span></td>
                                            <td><span>{investor?.leadInvestor}</span></td>
                                            <td><OtherInvestorAmount investor={investor} /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        <div className="lps-modal__mobile-list">
                            {activeRow?.otherInvestors?.map((investor, idx) => {
                                const isOpen = expandedRows.includes(investor?.key);
                                const col = MOB_BADGE_COLORS[idx % MOB_BADGE_COLORS.length];
                                const investorUrl = getEntityProfileUrl(investor);

                                return (
                                    <div
                                        key={investor?.key}
                                        className="mob-investment-row"
                                        onClick={() => toggleRow(investor?.key)}
                                    >
                                        <div className="mob-investment-row__top">
                                            <div className="mob-badge" style={{ background: col.bg, color: col.txt }}>
                                                {getInitials(investor?.name)}
                                            </div>
                                            <div className="mob-investment-row__main">
                                                {investorUrl ? (
                                                    <a
                                                        href={investorUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="structured-investments-table__entity-link"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        {investor?.name}
                                                    </a>
                                                ) : (
                                                    <span
                                                        className="structured-investments-table__entity-link startup-name"
                                                        data-action="expand-slider"
                                                        data-entityid={investor?.id}
                                                        data-ecosystemtype={investor?.type?.toLowerCase()}
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        {investor?.name}
                                                    </span>
                                                )}
                                                <div className="mob-investment-row__meta">{investor?.hq}</div>
                                            </div>
                                            <div className="mob-investment-row__right">
                                                <span className="mob-investment-row__amount">
                                                    <OtherInvestorAmount investor={investor} />
                                                </span>
                                                <div className="mob-investment-row__status-row">
                                                    <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} mob-investment-row__chevron`} />
                                                </div>
                                            </div>
                                        </div>

                                        {isOpen && (
                                            <div className="mob-investment-row__expand">
                                                <div className="mob-expand-grid">
                                                    <div className="mob-expand-cell">
                                                        <div className="mob-expand-cell__lbl">Fund Name</div>
                                                        <div className="mob-expand-cell__val">{investor?.fundName || '—'}</div>
                                                    </div>
                                                    <div className="mob-expand-cell">
                                                        <div className="mob-expand-cell__lbl">Lead Investor</div>
                                                        <div className="mob-expand-cell__val">{investor?.leadInvestor || '—'}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {contentScrollState.hasMoreBelow && (
                        <div
                            className="lps-modal__scroll-indicator"
                            aria-hidden="true"
                        >
                            <span>Scroll to view more</span>
                            <i className="fas fa-chevron-down"></i>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default OtherInvestorsModal;
